import React, { useEffect, useState } from "react";
import { useSettings } from "../contexts/SettingsContext";
import api from "../lib/api";

/**
 * Public legal pages — Terms / Privacy / Refund / Shipping.
 *
 * Each route renders <LegalPage slug="..."/>, which fetches the live content
 * from /api/legal/{slug} (auto-seeded server-side on first hit). Admins edit
 * sections from Admin → Legal pages without any code deploy.
 *
 * Content uses {{brand}} / {{brand_full}} / {{email}} placeholders that we
 * expand against the live settings doc at render time.
 */
function expand(text, vars) {
    return (text || "")
        .replace(/\{\{brand\}\}/g, vars.brand)
        .replace(/\{\{brand_full\}\}/g, vars.brandFull)
        .replace(/\{\{email\}\}/g, vars.email);
}

/** Minimal Markdown → React renderer. Handles **bold**, *italic*, `code`,
 *  bullet lists (-/•), inline mailto autolink, and paragraph breaks. */
function renderMd(text, email) {
    const lines = (text || "").split("\n");
    const out = [];
    let buf = [];
    let listBuf = null;

    const flushBuf = () => {
        if (!buf.length) return;
        out.push(<p key={`p-${out.length}`}>{renderInline(buf.join(" "), email)}</p>);
        buf = [];
    };
    const flushList = () => {
        if (!listBuf) return;
        out.push(
            <ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1">
                {listBuf.map((it, i) => <li key={i}>{renderInline(it, email)}</li>)}
            </ul>
        );
        listBuf = null;
    };

    for (const raw of lines) {
        const l = raw ?? "";
        const trimmed = l.trim();
        const isBullet = /^[-•]\s+/.test(trimmed);
        if (isBullet) {
            flushBuf();
            (listBuf = listBuf || []).push(trimmed.replace(/^[-•]\s+/, ""));
        } else if (trimmed === "") {
            flushBuf();
            flushList();
        } else {
            flushList();
            buf.push(trimmed);
        }
    }
    flushBuf();
    flushList();
    return out;
}

function renderInline(text, email) {
    // Inline tokens: **bold**, *italic*, `code`, and a brand email auto-link.
    const parts = [];
    let i = 0;
    const push = (node) => parts.push({ key: `n-${parts.length}`, node });
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b)/g;
    let m;
    while ((m = regex.exec(text)) !== null) {
        if (m.index > i) push(text.slice(i, m.index));
        const tok = m[0];
        if (tok.startsWith("**") && tok.endsWith("**")) push(<strong>{tok.slice(2, -2)}</strong>);
        else if (tok.startsWith("*") && tok.endsWith("*")) push(<em>{tok.slice(1, -1)}</em>);
        else if (tok.startsWith("`") && tok.endsWith("`")) push(<code className="bg-stone-100 px-1 text-[12px]">{tok.slice(1, -1)}</code>);
        else if (tok.includes("@")) push(<a href={`mailto:${tok}`} className="text-sky-700 underline">{tok}</a>);
        else push(tok);
        i = regex.lastIndex;
    }
    if (i < text.length) push(text.slice(i));
    return parts.map((p) => <React.Fragment key={p.key}>{p.node}</React.Fragment>);
}

export function LegalPage({ slug }) {
    const { settings } = useSettings();
    const [page, setPage] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let mounted = true;
        setPage(null);
        setErr(null);
        api.get(`/legal/${slug}`)
            .then(({ data }) => { if (mounted) setPage(data); })
            .catch((e) => { if (mounted) setErr(e?.response?.data?.detail || "Couldn't load page"); });
        return () => { mounted = false; };
    }, [slug]);

    const vars = {
        brand: settings?.brand_name || "Hahnemann",
        brandFull: settings?.brand_subtitle_full || "Pharmacy & Laboratory",
        email: settings?.contact_email || "help@hplmedicines.com",
    };

    if (err) {
        return (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-rose-50 border border-rose-200 p-4 text-sm text-rose-900">{err}</div>
            </div>
        );
    }
    if (!page) {
        return (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-sm text-stone-500">Loading…</div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid={`${slug}-page`}>
            <div className="label-eyebrow text-sky-700">Legal</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 mt-2 mb-8">{page.title}</h1>
            <p className="text-xs text-stone-500 mb-8">{page.last_updated_label || ""}</p>

            {(page.sections || []).map((s, i) => (
                <section key={i} className="mb-10" data-testid={`${slug}-section-${i}`}>
                    <h2 className="font-serif text-2xl sm:text-3xl text-slate-900 mb-3">{expand(s.title, vars)}</h2>
                    <div className="space-y-3 text-sm text-stone-700 leading-relaxed">
                        {renderMd(expand(s.body_md, vars), vars.email)}
                    </div>
                </section>
            ))}
        </div>
    );
}

// Compatibility wrappers — keep the old named exports so App.js routes
// don't need to change.
export const TermsPage = () => <LegalPage slug="terms" />;
export const PrivacyPage = () => <LegalPage slug="privacy" />;
export const RefundPage = () => <LegalPage slug="refund" />;
export const ShippingPage = () => <LegalPage slug="shipping" />;
